// src/pages/grow-business/cancel/[id].tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AppointmentDetails } from '@/components/scheduler/types';
import { FaCalendarTimes, FaExclamationTriangle } from 'react-icons/fa';

const CancelPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [appointment, setAppointment] = useState<AppointmentDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [cancelled, setCancelled] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';

    useEffect(() => {
        if (id) {
            fetchAppointment();
        }
    }, [id]);

    const fetchAppointment = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/api/booking/${id}`);
            if (!response.ok) {
                throw new Error('Appointment not found');
            }
            const data: AppointmentDetails = await response.json();

            // Check if already cancelled
            if (data.status === 'cancelled') {
                setCancelled(true);
            }

            setAppointment(data);
        } catch (err) {
            setError('Unable to find appointment. Please check your link and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async () => {
        if (!appointment || cancelling) return;

        setCancelling(true);
        setError(null);

        try {
            const response = await fetch(`${apiBaseUrl}/api/booking/cancel/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('Failed to cancel appointment');
            }

            setCancelled(true);
        } catch (err) {
            setError('Failed to cancel appointment. Please try again or contact support.');
        } finally {
            setCancelling(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e2ba57]"></div>
            </div>
        );
    }

    if (error && !appointment) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
                    <h1 className="text-2xl font-bold text-[#212A37] mb-4">Appointment Not Found</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <a
                        href="/grow-business"
                        className="inline-block bg-[#e2ba57] text-[#212A37] px-6 py-2 rounded-md font-semibold hover:bg-[#d1a43f] transition-colors"
                    >
                        Schedule New Appointment
                    </a>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Cancel Appointment | Swift Lead Digital</title>
            </Helmet>

            <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto">
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                        {!cancelled ? (
                            <>
                                <div className="bg-red-600 text-white px-6 py-4">
                                    <div className="flex items-center">
                                        <FaExclamationTriangle className="mr-3 text-2xl" />
                                        <h1 className="text-2xl font-bold">Cancel Appointment</h1>
                                    </div>
                                </div>

                                <div className="p-6 md:p-8">
                                    <div className="mb-6">
                                        <h2 className="text-lg font-semibold text-[#212A37] mb-4">
                                            Are you sure you want to cancel this appointment?
                                        </h2>

                                        {appointment && (
                                            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                                <p className="text-gray-700">
                                                    <strong>Appointment with:</strong> {appointment.customer_name}
                                                </p>
                                                <p className="text-gray-700">
                                                    <strong>Date & Time:</strong>{' '}
                                                    {new Date(appointment.appointment_time).toLocaleString()}
                                                </p>
                                            </div>
                                        )}

                                        {error && (
                                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
                                                {error}
                                            </div>
                                        )}

                                        <p className="text-gray-600 mb-6">
                                            This action cannot be undone. You'll need to schedule a new appointment if you change your mind.
                                        </p>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <button
                                            onClick={handleCancel}
                                            disabled={cancelling}
                                            className={`flex-1 px-6 py-3 rounded-md font-semibold transition-all ${cancelling
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-red-600 text-white hover:bg-red-700'
                                                }`}
                                        >
                                            {cancelling ? 'Cancelling...' : 'Yes, Cancel Appointment'}
                                        </button>

                                        <a
                                            href={`/grow-business/reschedule/${id}`}
                                            className="flex-1 text-center px-6 py-3 bg-[#e2ba57] text-[#212A37] rounded-md font-semibold hover:bg-[#d1a43f] transition-colors"
                                        >
                                            Reschedule Instead
                                        </a>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12 px-6">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                                    <FaCalendarTimes className="w-10 h-10 text-gray-500" />
                                </div>

                                <h2 className="text-2xl font-bold text-[#212A37] mb-2">
                                    Appointment Cancelled
                                </h2>
                                <p className="text-gray-600 mb-8">
                                    Your appointment has been cancelled. We've sent a confirmation email.
                                </p>

                                <div className="space-y-3">
                                    <a
                                        href="/grow-business"
                                        className="block bg-[#e2ba57] text-[#212A37] px-6 py-3 rounded-md font-semibold hover:bg-[#d1a43f] transition-colors"
                                    >
                                        Schedule New Appointment
                                    </a>

                                    <p className="text-sm text-gray-500">
                                        Questions? Email us at{' '}
                                        <a href="mailto:ralph.brooks@swiftleaddigital.com" className="text-[#e2ba57] hover:underline">
                                            ralph.brooks@swiftleaddigital.com
                                        </a>
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CancelPage;